// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FC, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ChartContainer } from '@shared/ui-kit/components';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type MontecarloSimulationResp } from '@shared/data-access/interfaces/api/ai/montecarlo';
import { SimulationChartTooltip } from './simulation-chart-tooltip';
import { transformSimulationData } from '../utils/transform-simulation-data';
import { useConst } from '@shared/ui-kit/hooks/use-const';

interface SimulationChartProps {
  data: MontecarloSimulationResp[];
  name: string;
  amount: number;
}

export const SimulationChart: FC<SimulationChartProps> = ({
  data,
  name,
  amount,
}) => {
  const chartData = useMemo(
    () => transformSimulationData(data, amount),
    [data, amount],
  );

  const colors = useConst(() => [
    '#9FADBE',
    '#3CB4EE',
    '#1072BA',
    '#3CB4EE',
    '#9FADBE',
  ]);

  return (
    <ChartContainer header={name} popupName="charts.returns" tooltip>
      <div className="relative h-full">
        <ResponsiveLine
          data={chartData}
          margin={{
            top: 25,
            right: 45,
            bottom: 60,
            left: 95,
          }}
          yScale={{
            type: 'linear',
            min: 0,
            max: 'auto',
          }}
          axisTop={null}
          axisRight={null}
          animate={false}
          axisBottom={{
            tickSize: 0,
            tickPadding: 20,
            tickValues: 6,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 15,
            tickValues: 6,
            format: /* istanbul ignore next */ value => formatToMoney(value),
          }}
          enableGridX={false}
          curve="basis"
          colors={colors}
          enablePoints={true}
          pointSize={8}
          enableArea={false}
          lineWidth={3}
          enableSlices={false}
          useMesh={true}
          enableCrosshair={false}
          tooltip={SimulationChartTooltip}
          theme={{
            axis: {
              ticks: {
                text: {
                  fontSize: 11,
                  fontFamily: 'poppins',
                  fill: '#515F70',
                },
              },
            },
            grid: {
              line: {
                stroke: '#DBDFEA',
                strokeWidth: 1,
                strokeDasharray: '8 12',
              },
            },
          }}
        />
      </div>
    </ChartContainer>
  );
};
